import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';

import { formatDate } from '../../common/formatter';
import StatusLabel from '../../common/StatusLabel';
import JiraPill from './components/JiraPill';
import { useGetJiraIssues, convertJiraStatus } from './Jira.hooks';

const StyledTableCell = styled(TableCell)`
  white-space: normal;
  word-wrap: break-word;
`;

const JiraIssuesList: React.FC<{ onError: (error: any) => void }> = ({
  onError
}) => {
  const history = useHistory();
  const [{ data, isLoading, error }, getIssues] = useGetJiraIssues();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam(
    'order',
    withDefault(StringParam, undefined)
  );
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    withDefault(StringParam, undefined)
  );

  useEffect(() => {
    getIssues(limit, page, order, orderDirection);
  }, [page, limit, getIssues, order, orderDirection]);

  useEffect(() => {
    if (error) {
      onError(error);
    }
  }, [error, onError]);

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'key'}
                  direction={getSortDirection('key')}
                  onClick={() => handleSort('key')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'summary'}
                  direction={getSortDirection('summary')}
                  onClick={() => handleSort('summary')}
                >
                  Summary
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'status'}
                  direction={getSortDirection('status')}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === '"Overall Priority"'}
                  direction={getSortDirection('"Overall Priority"')}
                  onClick={() => handleSort('"Overall Priority"')}
                >
                  Overall Priority
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === '"Overall Effort"'}
                  direction={getSortDirection('"Overall Effort"')}
                  onClick={() => handleSort('"Overall Effort"')}
                >
                  Overall Effort
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'assignee'}
                  direction={getSortDirection('assignee')}
                  onClick={() => handleSort('assignee')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'duedate'}
                  direction={getSortDirection('duedate')}
                  onClick={() => handleSort('duedate')}
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box
                    height={300}
                    width='100%'
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              data &&
              Array.isArray(data.issues) &&
              data.issues.map((issue: any) => (
                <TableRow
                  hover
                  key={issue.key}
                  onClick={() => history.push(`/portal/jira/${issue.key}`)}
                >
                  <TableCell>{issue.key}</TableCell>
                  <StyledTableCell>{issue.fields.summary}</StyledTableCell>
                  <TableCell>
                    <StatusLabel
                      status={convertJiraStatus(issue.fields.status.name)}
                    />
                  </TableCell>
                  <TableCell>
                    <JiraPill
                      priority={issue.fields?.customfield_10035?.value}
                    />
                  </TableCell>
                  <TableCell>
                    <JiraPill
                      priority={issue.fields?.customfield_10042?.value}
                    />
                  </TableCell>
                  <TableCell>
                    {issue.fields.assignee && issue.fields.assignee.displayName}
                  </TableCell>
                  <TableCell>{formatDate(issue.fields.duedate)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={data?.total || 0}
        rowsPerPage={data?.maxResults || 100}
        page={page ? Number(page) : 0}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setLimit(+e.target.value);
          setPage(0);
        }}
      />
    </>
  );
};

export default JiraIssuesList;
